import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trophy, Send, MailCheck } from 'lucide-react';
import { toast } from 'sonner';

function ScoreBar({ label, value, weight, color }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-24 text-muted-foreground shrink-0">{label} ({weight}%)</span>
      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(value, 100)}%` }}></div>
      </div>
      <span className="w-10 text-right font-medium">{Math.round(value)}</span>
    </div>
  );
}

const RANK_STYLES = ['bg-yellow-100 text-yellow-700', 'bg-slate-200 text-slate-700', 'bg-orange-100 text-orange-700'];

export const DistributionOrder = ({ tandaId, refreshKey }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/tandas/${tandaId}/distribution`);
      setData(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [tandaId, refreshKey]);

  const handleNotifyTurn = async (userId, fullName) => {
    setNotifying(userId);
    try {
      await api.post(`/tandas/${tandaId}/notify-turn?user_id=${userId}`);
      toast.success(`Se notificó a ${fullName} que es su turno de recibir el fondo`);
      fetchData();
    } catch (error) {
      toast.error('Error al enviar la notificación: ' + (error.response?.data?.detail || ''));
    } finally {
      setNotifying(null);
    }
  };

  return (
    <Card data-testid="distribution-order-card">
      <CardHeader>
        <CardTitle className="text-xl flex items-center gap-2"><Trophy className="text-primary" size={20} /> Orden de Distribución (ANEXO A)</CardTitle>
        <CardDescription>
          Puntaje = 30% Necesidad declarada + 50% Preparación (ciclos transcurridos con pago validado; 100 si la tanda aún no inicia) + 20% Lealtad (historial en la plataforma).
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-center py-6 animate-pulse text-muted-foreground text-sm">Calculando orden...</p>
        ) : !data || data.distribution.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6" data-testid="distribution-empty">
            Aún no hay participantes aprobades para calcular el orden.
          </p>
        ) : (
          <div className="space-y-3">
            {data.distribution.map((d) => (
              <div key={d.user_id} data-testid={`distribution-row-${d.user_id}`} className="flex items-center gap-4 p-4 rounded-xl bg-muted/40 border border-border">
                <div className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center font-bold ${RANK_STYLES[d.position - 1] || 'bg-primary/15 text-primary'}`}>
                  {d.position}
                </div>
                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm truncate">{d.full_name}</p>
                    {d.turn_notified_at && (
                      <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200 gap-1" data-testid={`turn-notified-badge-${d.user_id}`}>
                        <MailCheck size={12} /> Turno notificado {new Date(d.turn_notified_at).toLocaleDateString('es-MX')}
                      </Badge>
                    )}
                  </div>
                  <ScoreBar label="Necesidad" value={d.need_score} weight={30} color="bg-secondary" />
                  <ScoreBar label="Preparación" value={d.readiness_score} weight={50} color="bg-primary" />
                  <ScoreBar label="Lealtad" value={d.loyalty_score} weight={20} color="bg-violet-400" />
                </div>
                <div className="text-right shrink-0 space-y-2">
                  <div>
                    <p className="text-2xl font-black text-primary" data-testid={`distribution-score-${d.user_id}`}>{d.total_score}</p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">puntos</p>
                  </div>
                  <Button
                    size="sm"
                    variant={d.position === 1 ? 'default' : 'outline'}
                    className="rounded-full gap-1.5 text-xs"
                    data-testid={`notify-turn-btn-${d.user_id}`}
                    disabled={notifying === d.user_id}
                    onClick={() => handleNotifyTurn(d.user_id, d.full_name)}
                  >
                    <Send size={12} /> {notifying === d.user_id ? 'Enviando...' : d.turn_notified_at ? 'Reenviar' : 'Notificar turno'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
